import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { DialogEditKnowledgeComponent } from '@app/modules/knowledge/components/dialog-edit-knowledge/dialog-edit-knowledge.component';
import {
  KnowledgeAggregateItem,
  KnowledgeQuestion,
  KnowledgeSource,
  KnowledgeTopic,
  KnowledgeUploadProgress,
} from '@app/modules/knowledge/models/knowledge.interface';
import { KnowledgeService } from '@app/modules/knowledge/services/knowledge.service';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { Property } from '@app/shared/interfaces';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-knowledge',
  templateUrl: './knowledge.component.html',
  styles: [
    `
      @import 'variables';

      .expand-open {
        transform: scaleY(-1);
      }

      :host ::ng-deep .question-icon path,
      :host ::ng-deep .question-icon g {
        stroke: $grape-darker;
      }

      .custom-question-form-field ::ng-deep .mat-form-field-wrapper {
        padding: 0;
        margin: 0;
      }
    `,
  ],
})
export class KnowledgeComponent implements OnChanges {
  @Input() propertyId: Property['id'] | null = null;
  @ViewChild('newFileUploadInput') newFileUploadInput: ElementRef;
  @ViewChild('fileReuploadInput') fileReuploadInput: ElementRef;

  knowledge$ = this.knowledgeService.knowledge$;
  searchCriteria: BehaviorSubject<string> = new BehaviorSubject('');

  aiToggleOpen = false;
  sourcesToggleOpen = false;

  reuploadFileSource: KnowledgeSource | null = null;

  sourcesProcessing$ = this.knowledge$.pipe(
    map((knowledgeState) => {
      return knowledgeState.sources.some((source) => ! ['ready', 'error'].includes(source.state));
    })
  );
  sourcesProcessingMessage = 'Please wait while sources are processing...';

  filteredKnowledge$ = combineLatest([this.knowledge$, this.searchCriteria]).pipe(
    map(([knowledgeState, search]) => {
      if (!search.trim().length) {
        return knowledgeState.topics;
      }

      return knowledgeState.topics.filter((topic) => {
        return (
          topic.name.toLowerCase().includes(search.toLowerCase()) ||
          topic.aggregate_items.some((item) => item.content.toLowerCase().includes(search.toLowerCase()))
        );
      });
    })
  );

  customQuestionForm: UntypedFormGroup;

  customQuestion: KnowledgeQuestion | null = null;

  constructor(
    private knowledgeService: KnowledgeService,
    private dialog: MatDialog,
    private segmentIoService: SegmentIoService,
    private posthog: PosthogService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.customQuestionForm = this.fb.group({
      customQuestion: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['propertyId']) {
      this.fetchKnowledgeForProperty(this.propertyId);
    }
  }

  onTestResponse(question: KnowledgeQuestion) {
    question.computed_loading = true;

    this.knowledgeService.testResponse(this.propertyId, question.query).subscribe({
      next: (reply) => {
        question.computed_loading = false;
        question.computed_reply = reply;
      },
      error: () => {
        question.computed_loading = false;
        question.computed_reply = 'Sorry, an error occurred while processing your question.';
      },
    });
  }

  triggerNewFileUploadInput() {
    if (this.newFileUploadInput) {
      this.newFileUploadInput.nativeElement.click();
    } else {
      alert('newFileUploadInput is not defined');
    }
  }

  triggerReuploadFileInput(source: KnowledgeSource) {
    if (this.fileReuploadInput) {
      this.reuploadFileSource = source;
      this.fileReuploadInput.nativeElement.click();
    } else {
      alert('fileReuploadInput is not defined');
    }
  }

  fetchKnowledgeForProperty(propertyId: Property['id']) {
    return this.knowledgeService.fetchKnowledgeForProperty(propertyId);
  }

  onAddNewSourceClicked(fileInput: HTMLInputElement) {
    const file: File = fileInput.files[0];
    const reader = new FileReader();

    this.segmentIoService.track(SegmentEvent.KnowledgeHubImportStarted, {
      import_type: 'pdf',
    });

    reader.onload = () => {
      this.knowledgeService.uploadPDF(this.propertyId, file);
    };

    reader.readAsDataURL(file);
  }

  onReuploadSourceClicked(fileInput: HTMLInputElement) {
    const source = this.reuploadFileSource;
    const file: File = fileInput.files[0];
    const reader = new FileReader();

    this.segmentIoService.track(SegmentEvent.KnowledgeHubReimportStarted, {
      import_type: 'pdf',
    });

    reader.onload = () => {
      this.knowledgeService.reuploadPDF(this.propertyId, source, file);
      this.reuploadFileSource = null;
    };

    reader.readAsDataURL(file);
  }

  onDeleteSourceClicked(source: KnowledgeSource) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '800px',
      data: {
        name: 'this source',
        additionalText:
          'All knowledge items connected to this source will also be deleted, and the Knowledge Hub will be reprocessed to combine the remaining topics & items.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.knowledgeService.deleteSource(this.propertyId, source.id);
      }
    });
  }

  onImportMessageHistoryClicked() {
    this.segmentIoService.track(SegmentEvent.KnowledgeHubImportStarted, {
      import_type: 'message_history',
    });

    this.knowledgeService.generateFromMessageHistory(this.propertyId);
  }

  openNewItemDialog() {
    this.knowledge$
      .pipe(
        map((knowledgeState) => knowledgeState.topics),
        take(1)
      )
      .subscribe((topics) => {
        this.dialog.open(DialogEditKnowledgeComponent, {
          width: '800px',
          data: {
            propertyId: this.propertyId,
            topics,
          },
        });
      });
  }

  openEditItemDialog(item: KnowledgeAggregateItem, topic: KnowledgeTopic) {
    this.knowledge$
      .pipe(
        map((knowledgeState) => knowledgeState.topics),
        take(1)
      )
      .subscribe((topics) => {
        this.dialog.open(DialogEditKnowledgeComponent, {
          width: '800px',
          data: {
            propertyId: this.propertyId,
            item: item,
            topic: topic,
            topics: topics,
            lastTopicId: this.knowledgeService.lastTopicId,
          },
        });
      });
  }

  onDeleteItemClicked(item: KnowledgeAggregateItem) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '800px',
      data: {
        name: 'this knowledge item',
        additionalText: item.sources.map((source) => source.type).includes('touch_stay') // add this to resource instead?
          ? 'Note: this item is connected to Touch Stay, therefore you must also delete this information from your connected guidebook, otherwise this item will come back during the next resync.'
          : null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        item.deleting = true;

        this.deleteItem(item);
      }
    });
  }

  deleteItem(item: KnowledgeAggregateItem) {
    this.knowledgeService.deleteKnowledgeItem(item.id, this.propertyId);
  }

  trackProgressFn(index: number, item: KnowledgeUploadProgress) {
    return item.state;
  }

  triggerPosthogFeedback() {
    this.posthog.capture(SegmentEvent.KnowledgeHubImportSurveyTriggered, {
      import_type: 'pdf',
    });
  }

  aggregateItemSourceCount(aggregateItem: KnowledgeAggregateItem) {
    if (aggregateItem.sources.length === 1) {
      return aggregateItem.sources[0].name;
    }

    return aggregateItem.sources.map((source) => `- ${source.name}`).join('\n');
  }

  onAiToggle(event: { open: boolean }) {
    this.aiToggleOpen = event.open;

    if (this.aiToggleOpen) {
      // scroll to the top of the window
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  onSourcesToggle(event: { open: boolean }) {
    this.sourcesToggleOpen = event.open;
  }

  onAskCustomQuestion() {
    const customQuestionText = this.customQuestionForm.get('customQuestion')?.value?.trim();
    if (customQuestionText) {
      this.customQuestion = {
        query: customQuestionText,
        computed_loading: true,
        computed_reply: null,
      };
      this.segmentIoService.track(SegmentEvent.KnowledgeHubCustomQuestionAsked, {
        question: customQuestionText,
      });
      this.onTestResponse(this.customQuestion);
      this.customQuestionForm.reset();
    }
  }
}
